import React, { useState, useEffect, useRef, useCallback } from "react";
import { UsersThree, ListChecks, Lightbulb, Check, X, Sparkle } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { extractErrorMessage } from "@/lib/apiError";
import {
  parseCompetitive, assembleCompetitive, MAX_FEATURES, MAX_COMPETITORS,
} from "@/lib/competitive";

/**
 * Structured editor for slide 8 (Competitive Landscape) — feature-comparison
 * matrix. Column 1: up to 8 features the business provides (own column is
 * always a tick). Up to 3 competitors, each with a per-feature ✓/✗ toggle.
 * Takeaway box at the bottom with a 1-credit AI draft button.
 */

const C = {
  border: "rgba(255,255,255,0.08)",
  ink: "#F5F7FA",
  muted: "rgba(255,255,255,0.55)",
  bg: "#0F1B22",
  bgSoft: "#132029",
  good: "#10B981",
  bad: "#F87171",
};

function parseToLocal(content) {
  const p = parseCompetitive(content || "");
  let features = (p.features || []).map((f) => ({
    text: f.text || "",
    marks: [...(f.marks || [])].concat([false, false, false]).slice(0, MAX_COMPETITORS),
  }));
  // Legacy fallback — old bullet-style slide 8 content becomes feature rows.
  if (features.length === 0 && (content || "").trim() && !/^competitors$/im.test(content)) {
    features = (content || "").split(/\n/)
      .map((l) => l.replace(/^[-*•●▪]\s*/, "").replace(/^\d+[.)]\s*/, "").trim())
      .filter(Boolean)
      .slice(0, MAX_FEATURES)
      .map((text) => ({ text, marks: [false, false, false] }));
  }
  while (features.length < MAX_FEATURES) features.push({ text: "", marks: [false, false, false] });
  const competitors = [...(p.competitors || [])];
  while (competitors.length < MAX_COMPETITORS) competitors.push("");
  return {
    competitors: competitors.slice(0, MAX_COMPETITORS),
    features: features.slice(0, MAX_FEATURES),
    takeaway: p.takeaway || "",
  };
}

export default function CompetitiveForm({ content, onChange, primary, deckId, companyName }) {
  const [competitors, setCompetitors] = useState(() => parseToLocal(content).competitors);
  const [features, setFeatures] = useState(() => parseToLocal(content).features);
  const [takeaway, setTakeaway] = useState(() => parseToLocal(content).takeaway);
  const [drafting, setDrafting] = useState(false);
  const lastEmittedRef = useRef(content || "");

  useEffect(() => {
    if ((content || "") === lastEmittedRef.current) return;
    const next = parseToLocal(content);
    setCompetitors(next.competitors);
    setFeatures(next.features);
    setTakeaway(next.takeaway);
    lastEmittedRef.current = content || "";
  }, [content]);

  const emit = useCallback((nextComps, nextFeats, nextTakeaway) => {
    const assembled = assembleCompetitive({
      competitors: nextComps,
      features: nextFeats.filter((f) => (f.text || "").trim()),
      takeaway: nextTakeaway,
    });
    lastEmittedRef.current = assembled;
    onChange(assembled);
  }, [onChange]);

  const updateCompetitor = (i, v) => {
    const next = competitors.map((c, idx) => (idx === i ? v : c));
    setCompetitors(next);
    emit(next, features, takeaway);
  };
  const updateFeatureText = (i, v) => {
    const next = features.map((f, idx) => (idx === i ? { ...f, text: v } : f));
    setFeatures(next);
    emit(competitors, next, takeaway);
  };
  const toggleMark = (fi, ci) => {
    const next = features.map((f, idx) => {
      if (idx !== fi) return f;
      const marks = [...f.marks];
      marks[ci] = !marks[ci];
      return { ...f, marks };
    });
    setFeatures(next);
    emit(competitors, next, takeaway);
  };
  const updateTakeaway = (v) => {
    setTakeaway(v);
    emit(competitors, features, v);
  };

  const draftTakeaway = async () => {
    const comps = competitors.filter((c) => (c || "").trim());
    const feats = features.filter((f) => (f.text || "").trim());
    if (comps.length === 0 || feats.length === 0) {
      toast.error("Add at least one feature and one competitor first.");
      return;
    }
    setDrafting(true);
    try {
      const { data } = await api.post(`/pitch/decks/${deckId}/competitive/takeaway`, {
        competitors: comps,
        features: feats.map((f) => ({ text: f.text, marks: f.marks.slice(0, comps.length) })),
      });
      updateTakeaway(data.text || "");
      window.dispatchEvent(new Event("clinq:credits-changed"));
      toast.success("Takeaway drafted — 1 credit used.");
    } catch (err) {
      if (!err.isCreditShortage) toast.error(extractErrorMessage(err, "Could not draft the takeaway"));
    } finally {
      setDrafting(false);
    }
  };

  const activeComps = competitors.map((c) => (c || "").trim());

  return (
    <div data-testid="competitive-form" className="space-y-4"
      style={{ fontFamily: "'Raleway','Manrope',system-ui,sans-serif" }}>
      <div className="p-3 rounded-md text-[13px] leading-relaxed"
        style={{ backgroundColor: `${primary}14`, border: `1px solid ${primary}33`, color: C.ink }}>
        <strong className="font-extrabold">Competitive Landscape</strong> renders as a comparison table — up to {MAX_FEATURES} things your customers need (your column always gets a tick) against up to {MAX_COMPETITORS} competitors. Tap a competitor's cell to toggle ✓/✗.
      </div>

      {/* Competitors */}
      <div className="p-4 rounded-lg" style={{ backgroundColor: C.bgSoft, border: `1px solid ${C.border}` }}>
        <div className="flex items-center gap-2.5 mb-3">
          <span className="inline-flex items-center justify-center rounded-full shrink-0"
            style={{ width: 32, height: 32, backgroundColor: primary, color: "#fff" }}>
            <UsersThree size={16} weight="fill" />
          </span>
          <div className="flex-1 min-w-0">
            <div className="text-[12px] font-extrabold uppercase tracking-[0.20em]" style={{ color: primary }}>
              Competitors
            </div>
            <div className="text-[12px]" style={{ color: C.muted }}>
              Up to {MAX_COMPETITORS} alternatives funders will compare you against.
            </div>
          </div>
        </div>
        <div className="grid grid-cols-3 gap-2">
          {competitors.map((c, i) => (
            <input key={i} type="text" data-testid={`competitive-competitor-${i}`}
              value={c} onChange={(e) => updateCompetitor(i, e.target.value)}
              placeholder={`Competitor ${i + 1}`}
              className="px-3 py-2 rounded text-[13px] focus:outline-none"
              style={{ backgroundColor: C.bg, border: `1px solid ${C.border}`, color: C.ink }} />
          ))}
        </div>
      </div>

      {/* Feature matrix */}
      <div className="p-4 rounded-lg" style={{ backgroundColor: C.bgSoft, border: `1px solid ${C.border}` }}>
        <div className="flex items-center gap-2.5 mb-3">
          <span className="inline-flex items-center justify-center rounded-full shrink-0"
            style={{ width: 32, height: 32, backgroundColor: primary, color: "#fff" }}>
            <ListChecks size={16} weight="fill" />
          </span>
          <div className="flex-1 min-w-0">
            <div className="text-[12px] font-extrabold uppercase tracking-[0.20em]" style={{ color: primary }}>
              What customers need
            </div>
            <div className="text-[12px]" style={{ color: C.muted }}>
              Up to {MAX_FEATURES} solutions your business provides. Your column is always ✓.
            </div>
          </div>
        </div>
        {/* Column header strip */}
        <div className="flex items-center gap-2 mb-1.5 pl-1">
          <div className="flex-1" />
          <div className="w-9 text-center text-[9px] font-extrabold uppercase tracking-wider truncate"
            style={{ color: "#2CC5A8" }} title={companyName || "You"}>
            {(companyName || "You").slice(0, 6)}
          </div>
          {activeComps.map((c, i) => (
            <div key={i} className="w-9 text-center text-[9px] font-bold uppercase tracking-wider truncate"
              style={{ color: c ? C.muted : "rgba(255,255,255,0.2)" }} title={c || `Competitor ${i + 1}`}>
              {(c || "—").slice(0, 6)}
            </div>
          ))}
        </div>
        <div className="space-y-1.5">
          {features.map((f, fi) => (
            <div key={fi} className="flex items-center gap-2">
              <input type="text" data-testid={`competitive-feature-${fi}`}
                value={f.text} onChange={(e) => updateFeatureText(fi, e.target.value)}
                placeholder={fi === 0 ? "e.g. Automated investor documents" : "Another thing customers need…"}
                className="flex-1 min-w-0 px-3 py-2 rounded text-[13px] focus:outline-none"
                style={{ backgroundColor: C.bg, border: `1px solid ${C.border}`, color: C.ink }} />
              {/* Own column — always a tick */}
              <span data-testid={`competitive-own-tick-${fi}`}
                className="w-9 h-8 shrink-0 inline-flex items-center justify-center rounded"
                style={{
                  backgroundColor: "rgba(44,197,168,0.14)",
                  border: "1px solid rgba(44,197,168,0.4)",
                  opacity: (f.text || "").trim() ? 1 : 0.25,
                }}>
                <Check size={14} weight="bold" style={{ color: C.good }} />
              </span>
              {activeComps.map((c, ci) => {
                const enabled = !!c && (f.text || "").trim();
                const on = !!f.marks[ci];
                return (
                  <button key={ci} type="button"
                    data-testid={`competitive-mark-${fi}-${ci}`}
                    disabled={!enabled}
                    onClick={() => toggleMark(fi, ci)}
                    title={enabled ? `${c}: ${on ? "offers this" : "doesn't offer this"} — tap to toggle` : "Name the competitor first"}
                    className="w-9 h-8 shrink-0 inline-flex items-center justify-center rounded transition-colors disabled:opacity-20"
                    style={{
                      backgroundColor: on ? "rgba(44,197,168,0.14)" : "rgba(248,113,113,0.10)",
                      border: `1px solid ${on ? "rgba(44,197,168,0.4)" : "rgba(248,113,113,0.35)"}`,
                    }}>
                    {on
                      ? <Check size={14} weight="bold" style={{ color: C.good }} />
                      : <X size={14} weight="bold" style={{ color: C.bad }} />}
                  </button>
                );
              })}
            </div>
          ))}
        </div>
      </div>

      {/* Takeaway */}
      <div className="p-4 rounded-lg" style={{ backgroundColor: C.bgSoft, border: `1px solid ${C.border}` }}>
        <div className="flex items-center gap-2.5 mb-3">
          <span className="inline-flex items-center justify-center rounded-full shrink-0"
            style={{ width: 32, height: 32, backgroundColor: "#ECC764", color: "#004B62" }}>
            <Lightbulb size={16} weight="fill" />
          </span>
          <div className="flex-1 min-w-0">
            <div className="text-[12px] font-extrabold uppercase tracking-[0.20em]" style={{ color: "#ECC764" }}>
              The takeaway
            </div>
            <div className="text-[12px]" style={{ color: C.muted }}>
              One sentence on why you win — shown in a highlight band at the bottom of the slide.
            </div>
          </div>
          <button type="button" data-testid="competitive-takeaway-ai"
            onClick={draftTakeaway} disabled={drafting}
            className="shrink-0 inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider px-3 py-2 rounded-md disabled:opacity-50 transition-colors"
            style={{ backgroundColor: `${primary}22`, color: "#7BD3CE", border: `1px solid ${primary}55` }}>
            <Sparkle size={12} weight="fill" />
            {drafting ? "Drafting…" : "Draft with AI · 1 cr"}
          </button>
        </div>
        <textarea rows={2} data-testid="competitive-takeaway"
          value={takeaway} onChange={(e) => updateTakeaway(e.target.value)}
          placeholder="e.g. We're the only platform that builds your documents, scores your readiness and matches you to capital — in days, with no equity taken."
          className="w-full px-3 py-2 rounded text-[13px] leading-relaxed focus:outline-none"
          style={{ backgroundColor: C.bg, border: `1px solid ${C.border}`, color: C.ink, resize: "vertical" }} />
      </div>
    </div>
  );
}
